/**
 * Пошук по довідці у шапці сайту + кнопка переходу у довідник (ч.1000).
 *
 * Вимога власника: поле пошуку ПЕРЕД іконкою масштабу шрифта і знак
 * питання для переходу у розділ довідки.
 *
 * Пошук поки ЛИШЕ по довіднику (рішення власника): набираєш — випадають
 * статті, Enter відкриває `/help` з цим запитом. Глобальний пошук по
 * документах — окрема задача (потребує врахування прав і організацій).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { help, type HelpArticleListItem } from '@/api/help';

export default function HeaderHelpSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [items, setItems] = useState<HelpArticleListItem[]>([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  // Пошук із затримкою — не смикаємо сервер на кожну літеру.
  useEffect(() => {
    const text = q.trim();
    if (text.length < 2) { setItems([]); return undefined; }
    const t = setTimeout(() => {
      help.articles({ q: text })
        .then((r) => { setItems(r.slice(0, 8)); setHi(0); })
        .catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const go = (slug?: string) => {
    setOpen(false);
    setQ('');
    navigate(slug ? `/help/${slug}` : `/help?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="help-search" ref={rootRef}>
      <div className="help-search__field">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" />
        </svg>
        <input
          type="search"
          name="help-search"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          data-lpignore="true"
          data-form-type="other"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault(); setHi((h) => Math.min(h + 1, items.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault(); setHi((h) => Math.max(h - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              go(items[hi]?.slug);
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
          placeholder="Пошук у довідці"
          aria-label="Пошук у довідці"
        />
      </div>

      {open && q.trim().length >= 2 && (
        <div className="help-search__pop">
          {items.length === 0 ? (
            <div className="help-search__empty">Нічого не знайдено</div>
          ) : (
            <>
              {items.map((a, i) => (
                <button key={a.slug} type="button"
                  className={`help-search__item${i === hi ? ' is-hi' : ''}`}
                  onMouseEnter={() => setHi(i)}
                  onMouseDown={(e) => { e.preventDefault(); go(a.slug); }}>
                  <span className="help-search__title">{a.title}</span>
                  <span className="help-search__section">{a.section}</span>
                </button>
              ))}
              <button type="button" className="help-search__all"
                onMouseDown={(e) => { e.preventDefault(); go(); }}>
                усі результати у довіднику →
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Знак питання — перехід у довідковий центр.
 *
 * ч.1009: дорогою запамʼятовуємо сторінку, з якої людина попросила
 * допомоги. Якщо звідси вона піде у підтримку, звернення буде привʼязане
 * до цієї сторінки — точніші підказки статей і видно, де довідки бракує.
 */
export function HeaderHelpButton() {
  const navigate = useNavigate();
  const remember = () => {
    try {
      const path = window.location.pathname.replace(/^\//, '');
      if (path && !path.startsWith('help') && !path.startsWith('support')) {
        sessionStorage.setItem('support:lastRoute', path);
      }
    } catch { /* приватне вікно — не критично */ }
  };
  return (
    <button type="button" className="app__iconbtn" title="Довідка"
      aria-label="Довідка" onClick={() => { remember(); navigate('/help'); }}>
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
        <path d="M9.2 9a2.8 2.8 0 1 1 3.3 3.2c-.6.2-1 .8-1 1.4v.4"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="11.5" cy="17" r="1" fill="currentColor" />
      </svg>
    </button>
  );
}
